import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmDialog, Icon } from '@/shared/ui';

export interface ArchiveToggleButtonProps {
  /** Aktuální stav archivace entity. */
  isArchived: boolean;
  /** Vyvolá archivaci (po potvrzení dialogu). */
  onArchive: () => void;
  /** Vyvolá obnovení (de-archivaci). */
  onUnarchive: () => void;
  /** Zakázat tlačítko (např. během odesílání jiné operace). */
  disabled?: boolean;
}

/**
 * Malé tlačítko pro (de)archivaci entity. Archivace se potvrzuje dialogem,
 * obnovení je přímé. Sdílené napříč entitami.
 */
export function ArchiveToggleButton({
  isArchived,
  onArchive,
  onUnarchive,
  disabled,
}: ArchiveToggleButtonProps) {
  const { t } = useTranslation('archival');
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isArchived) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={onUnarchive}
        data-testid="unarchive-button"
      >
        <Icon name="refresh" size={14} />
        {t('actions.unarchive')}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => setConfirmOpen(true)}
        data-testid="archive-button"
      >
        <Icon name="folder" size={14} />
        {t('actions.archive')}
      </Button>

      {confirmOpen ? (
        <ConfirmDialog
          title={t('confirm.archiveTitle')}
          message={t('confirm.archiveMessage')}
          confirmLabel={t('confirm.confirmArchive')}
          cancelLabel={t('confirm.cancel')}
          confirmVariant="primary"
          onConfirm={() => {
            setConfirmOpen(false);
            onArchive();
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      ) : null}
    </>
  );
}
